import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      setScrolled(currentY > 50);

      if (Math.abs(delta) > 6) {
        if (currentY <= 0) setHidden(false);
        else if (delta > 0) setHidden(true); // down -> hide
        else setHidden(false);               // up -> show
        lastScrollY.current = currentY;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Who We Are", path: "/about" },
    { name: "Contact Us", path: "/contact" },
    { name: "FAQs", path: "/ourbeers" },
  ];

  return (
    <nav
      className={[
        "fixed top-0 left-0 right-0 z-50 transition-transform duration-500 ease-in-out",
        hidden ? "-translate-y-full" : "translate-y-0",
        scrolled ? "bg-black/70 md:backdrop-blur-md shadow-md" : "bg-black/40 md:backdrop-blur-sm",
      ].join(" ")}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="shrink-0">
          {/* smaller on mobile, same  as before on md+ */}
          <img src="/liqlogo.png" alt="Logo" className="h-14 md:h-24" />
        </Link>

        {/* Desktop menu (unchanged) */}
        <ul className="hidden md:flex space-x-8 font-body font-semibold">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <li key={link.name}>
                <Link
                  to={link.path}
                  className={`transition-colors duration-300 ${
                    isActive ? "text-white" : "text-white hover:text-gray-400"
                  }`}
                >
                  {link.name}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Mobile burger */}
        <button
          type="button"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((o) => !o)}
          className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white/50 relative z-[60]"
        >
          {/* Icon */}
          <svg
            className={`h-7 w-7 transition-transform duration-200 ${mobileOpen ? "rotate-90" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile panel */}
      <div
        className={[
          "md:hidden fixed left-0 right-0 top-[56px] z-50", // 56px ≈ h-14 logo; adjust if needed
          "bg-black/90",
          "transition-transform transition-opacity duration-300 ease-out",
          mobileOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-4 pointer-events-none", // completely non-interactive when closed
        ].join(" ")}
      >
        <div className="px-4 py-4">
          <ul className="space-y-3 font-body font-semibold antialiased">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={() => setMobileOpen(false)} // close on tap
                    className={[
                      "block rounded-lg px-3 py-2 transition-colors",
                      isActive
                        ? "text-white bg-white/10"
                        : "text-white hover:text-gray-300 hover:bg-white/10",
                    ].join(" ")}
                  >
                    {link.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Tap-to-close overlay for mobile (below panel) */}
      {mobileOpen && (
        <button
          aria-label="Close menu overlay"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 top-[56px] z-40 bg-black/40"
        />
      )}
    </nav>
  );
}
